import { ExploreColumnType } from "../explore/columns/ExploreColum.model";
import { ExploreInteractionRow } from "../explore/Explore.model";
import { Filter } from "../explore/Explore.filter";
import { Sources } from "./Tracker.api";

export const trackerStages = [
  "to_do",
  "in_progress",
  "done",
  "hidden",
] as const;
export type TrackerStage = (typeof trackerStages)[number];

export interface BaseTracker {
  id: string;
  name: string;
  matchRules?: object;
  isArchived?: boolean;
  sources?: Sources[];
  itemsCount: number;
  itemsCountByStage: Record<TrackerStage, number>;
  alertConfig?: TrackerAlertConfig | null;
}

export interface Tracker extends BaseTracker {
  columns: TrackerColumn[];
  fields: TrackerField[];
  matchRules?: object;
  sources?: Sources[];
}

export interface TrackerColumn {
  id: string;
  name: string;
  stage: TrackerStage;
  order?: number;
  tags?: string[];
}

export interface TrackerField {
  field: TrackerFieldType;
}

export interface TrackerItem {
  id: string;
  ticketId?: string | null;
  conversationId?: string | null;
  comment: string;
  stage: TrackerStage;
  interactionData: ExploreInteractionRow;
  events?: TrackerItemEvent[];
  tracker?: Tracker;
  tags?: string[];
  columnId: string;
}

const trackerItemEventTypes = [
  "captured_by_tracker",
  "stage_change",
  "column_change",
  "comment",
] as const;
export type TrackerItemEventType = (typeof trackerItemEventTypes)[number];

export interface TrackerItemEvent {
  type: TrackerItemEventType;
  value?: string;
  createdAt: Date | null;
}

export type TrackerAlertAction =
  | { type: "slack" }
  | { type: "webhook"; webhookUrl: string };

export const trackerAlertWindowMinutesOptions = [
  10, 15, 30, 60, 90, 120,
] as const;
export type TrackerAlertWindowMinutes =
  (typeof trackerAlertWindowMinutesOptions)[number];

export interface TrackerAlertConfig {
  thresholdCount: number;
  timeWindowMinutes: TrackerAlertWindowMinutes;
  additionalConditions?: Filter | null;
  actions: TrackerAlertAction[];
}

export const isAlertWindowMinutes = (
  value: number
): value is TrackerAlertWindowMinutes => {
  return (trackerAlertWindowMinutesOptions as readonly number[]).includes(value);
};

export enum TrackerFieldType {
  id = "id",
  displayId = "displayId",
  subject = "subject",
  createdAt = "createdAt",
  updatedAt = "updatedAt",
  status = "status",
  channel = "channel",
  topic = "topic",
  subTopic = "subTopic",
  category = "category",
  agentAssigned = "agentAssigned",
  team = "team",
  avgQaScore = "avgQaScore",
  avgSentiment = "avgSentiment",
  csat = "csat",
  countPublicMessages = "countPublicMessages",
  countInternalMessages = "countInternalMessages",
  timeToResolution = "timeToResolution",
  timeToFirstResponse = "timeToFirstResponse",
  resolutionStatus = "resolutionStatus",
  escalationReason = "escalationReason",
  skipReason = "skipReason",
  resolvedReason = "resolvedReason",
  countQuackMessages = "countQuackMessages",
  isQuackEscalated = "isQuackEscalated",
  collaborators = "collaborators",
  isFiniteState = "isFiniteState",
}

// TODO: Remove this once we have a proper mapping
export const trackerFieldTypeToExploreColumnType: Record<
  TrackerFieldType,
  ExploreColumnType
> = {
  [TrackerFieldType.id]: "id",
  [TrackerFieldType.displayId]: "displayId",
  [TrackerFieldType.subject]: "subject",
  [TrackerFieldType.createdAt]: "createdAt",
  [TrackerFieldType.updatedAt]: "updatedAt",
  [TrackerFieldType.status]: "status",
  [TrackerFieldType.channel]: "channel",
  [TrackerFieldType.topic]: "topic",
  [TrackerFieldType.subTopic]: "subTopic",
  [TrackerFieldType.category]: "category",
  [TrackerFieldType.agentAssigned]: "agentAssigned",
  [TrackerFieldType.team]: "team",
  [TrackerFieldType.avgQaScore]: "avgQaScore",
  [TrackerFieldType.avgSentiment]: "avgSentiment",
  [TrackerFieldType.csat]: "csat",
  [TrackerFieldType.countPublicMessages]: "countPublicMessages",
  [TrackerFieldType.countInternalMessages]: "countInternalMessages",
  [TrackerFieldType.timeToResolution]: "timeToResolution",
  [TrackerFieldType.timeToFirstResponse]: "timeToFirstResponse",
  [TrackerFieldType.resolutionStatus]: "resolutionStatus",
  [TrackerFieldType.escalationReason]: "escalationReason",
  [TrackerFieldType.skipReason]: "skipReason",
  [TrackerFieldType.resolvedReason]: "resolvedReason",
  [TrackerFieldType.countQuackMessages]: "countQuackMessages",
  [TrackerFieldType.isQuackEscalated]: "isQuackEscalated",
  [TrackerFieldType.collaborators]: "collaborators",
  [TrackerFieldType.isFiniteState]: "isFiniteState",
};

// Custom sort for tracker columns
export const TrackerColumnSort = (a: TrackerColumn, b: TrackerColumn) => {
  // 1. Captured always first
  if (a.name === "Captured") return -1;
  if (b.name === "Captured") return 1;

  // 2. to_do columns (excluding Captured) next
  if (a.stage === "to_do" && b.stage !== "to_do") return -1;
  if (a.stage !== "to_do" && b.stage === "to_do") return 1;

  // 3. in_progress columns next
  if (a.stage === "in_progress" && b.stage !== "in_progress") return -1;
  if (a.stage !== "in_progress" && b.stage === "in_progress") return 1;

  // 4. done columns last
  if (a.stage === "done" && b.stage !== "done") return 1;
  if (a.stage !== "done" && b.stage === "done") return -1;

  // 5. Within the same stage, sort by order field
  if (a.stage === b.stage) {
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }
    // If one has order and the other doesn't, prioritize the one with order
    if (a.order !== undefined && b.order === undefined) return -1;
    if (a.order === undefined && b.order !== undefined) return 1;
  }

  return 0;
};
